import { ModelSetTransformer, ModelTransformer } from '../common/common.model';

// DataConfig.Lookup_PerformanceDataViewType
export enum PerformanceDataViewType {
    MARKET = 1,
    SITE,
    PEER_GROUP,
    SITE_PEER_GROUP,
    SUPER_GROUP,
    SITE_SUPER_GROUP,
    USER_SUPER_GROUP,
    AREA_PEER_GROUP,
    REGION_PEER_GROUP,
    AREA_SUPER_GROUP,
    REGION_SUPER_GROUP,
    AREA_LOB,
    REGION_LOB,
    TEAM_SUPER_GROUP,
    AREA,
    REGION,
    USER, // userId/managerId
    SITE_LOB,
    LOB = 20,
    USER_QUEUE,
    SUPER_GROUP_QUEUE,
    SITE_SUPER_GROUP_QUEUE,
    QUEUE,
}

// DataConfig.Lookup_CriteriaIDType
export enum CriteriaIdType {
    USER_ID = 1,
    SUPER_GROUP_ID,
    PEER_GROUP_ID,
    SITE_ID,
    AREA_ID,
    REGION_ID,
    LOB_ID,
    QUEUE_ID,
    TEAM_ID, // managerId/userId,
    QUEUE_ID_2 = 36, // sometimes it's this or the other
    USER_ROLE_ID, // special usage
    TEAM_ROLE_ID, // special usage
    HIERARCHY, // special usage
}

export interface PerformanceDataViewTypeArgs {
    [key: number]: {
        primary: CriteriaIdType;
        secondary: CriteriaIdType;
        tertiary: CriteriaIdType;
    };
}

// DataConfig.Lookup_PerformanceDataViewType
export const PerformanceDataViewTypeArgsMap: PerformanceDataViewTypeArgs = {
    [PerformanceDataViewType.MARKET]: {
        primary: null,
        secondary: null,
        tertiary: null,
    },
    [PerformanceDataViewType.SITE]: {
        primary: CriteriaIdType.SITE_ID,
        secondary: null,
        tertiary: null,
    },
    [PerformanceDataViewType.PEER_GROUP]: {
        primary: CriteriaIdType.PEER_GROUP_ID,
        secondary: null,
        tertiary: null,
    },
    [PerformanceDataViewType.SITE_PEER_GROUP]: {
        primary: CriteriaIdType.SITE_ID,
        secondary: CriteriaIdType.PEER_GROUP_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.SUPER_GROUP]: {
        primary: CriteriaIdType.SUPER_GROUP_ID,
        secondary: null,
        tertiary: null,
    },
    [PerformanceDataViewType.SITE_SUPER_GROUP]: {
        primary: CriteriaIdType.SITE_ID,
        secondary: CriteriaIdType.SUPER_GROUP_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.USER_SUPER_GROUP]: {
        primary: CriteriaIdType.USER_ID,
        secondary: CriteriaIdType.SUPER_GROUP_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.AREA_PEER_GROUP]: {
        primary: CriteriaIdType.AREA_ID,
        secondary: CriteriaIdType.PEER_GROUP_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.REGION_PEER_GROUP]: {
        primary: CriteriaIdType.REGION_ID,
        secondary: CriteriaIdType.PEER_GROUP_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.AREA_SUPER_GROUP]: {
        primary: CriteriaIdType.AREA_ID,
        secondary: CriteriaIdType.SUPER_GROUP_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.REGION_SUPER_GROUP]: {
        primary: CriteriaIdType.REGION_ID,
        secondary: CriteriaIdType.SUPER_GROUP_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.AREA_LOB]: {
        primary: CriteriaIdType.AREA_ID,
        secondary: CriteriaIdType.LOB_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.REGION_LOB]: {
        primary: CriteriaIdType.REGION_ID,
        secondary: CriteriaIdType.LOB_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.TEAM_SUPER_GROUP]: {
        primary: CriteriaIdType.TEAM_ID,
        secondary: CriteriaIdType.SUPER_GROUP_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.AREA]: {
        primary: CriteriaIdType.AREA_ID,
        secondary: null,
        tertiary: null,
    },
    [PerformanceDataViewType.REGION]: {
        primary: CriteriaIdType.REGION_ID,
        secondary: null,
        tertiary: null,
    },
    [PerformanceDataViewType.USER]: {
        primary: CriteriaIdType.USER_ID,
        secondary: null,
        tertiary: null,
    },
    [PerformanceDataViewType.SITE_LOB]: {
        primary: CriteriaIdType.SITE_ID,
        secondary: CriteriaIdType.LOB_ID,
        tertiary: null,
    },
    19: { primary: null, secondary: null, tertiary: null }, // removed
    [PerformanceDataViewType.LOB]: {
        primary: CriteriaIdType.LOB_ID,
        secondary: null,
        tertiary: null,
    },
    [PerformanceDataViewType.USER_QUEUE]: {
        primary: CriteriaIdType.USER_ID,
        secondary: CriteriaIdType.QUEUE_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.SUPER_GROUP_QUEUE]: {
        primary: CriteriaIdType.SUPER_GROUP_ID,
        secondary: CriteriaIdType.QUEUE_ID,
        tertiary: null,
    },
    [PerformanceDataViewType.SITE_SUPER_GROUP_QUEUE]: {
        primary: CriteriaIdType.SITE_ID,
        secondary: CriteriaIdType.SUPER_GROUP_ID,
        tertiary: CriteriaIdType.QUEUE_ID,
    },
    [PerformanceDataViewType.QUEUE]: {
        primary: CriteriaIdType.QUEUE_ID,
        secondary: null,
        tertiary: null,
    },
};

interface ExtMetricDataItem {
    performanceViewTypeId: PerformanceDataViewType;
    eventDate: string;
    primaryType: number;
    primaryId: number;
    primaryName: string;
    primaryShortName: string;
    primaryInitials: string;
    secondaryType: number;
    secondaryId: number;
    secondaryName: string;
    secondaryShortName: string;
    secondaryInitials: string;
    tertiaryType: number;
    tertiaryId: number;
    tertiaryName: string;
    tertiaryShortName: string;
    tertiaryInitials: string;
    metricID: number;
    metricName: string;
    metricShortName: string;
    metricInitials: string;
    actual: number;
    goal: number;
    goalThresholdColour: string;
    goalThresholdName: string;
    previousActual: number;
    rankOrder: number;
    target: number;
    targetThresholdColour: string;
    targetThresholdName: string;
    trend: string;
    trendName: string;
    projectedActual: number;
    projectedTarget: number;
    gap: number;
    projectedPctToTgt: number;
}

export interface MetricDataItem {
    id: number;
    name: string;
    shortName: string;
    initials: string;
    eventDate: Date;
    actual: number;
    target: number;
    previousActual: number;
    projectedActual: number;
    projectedTarget: number;
    projectedPctToTgt: number;
    gap: number;
    goal: number;
    rankOrder: number;
    targetThresholdColor: string;
    targetThresholdBold: boolean;
    targetThresholdName: string;
    trendClass: string;
    trendName: string;
    displayId: number;
    displayName: string;
    displayShortName: string;
    displayInitials: string;
    dataId: number; // TODO remove after old pi is removed
    dataIds: { [criteriaIdType: number]: number };
    primaryId: number;
    secondaryId: number;
    tertiaryId: number;
    // last mile
    percentToTarget: number;
    objectiveMet: boolean;
    weight: number; // TODO revise. get this moved to sp
    weightedScore: number;
}

export const NO_DATA_ID = -9000;

export interface MetricData {
    id: number;
    name: string;
    shortName: string;
    initials: string;
    dataId: number;
    metrics: MetricDataItem[];
    displayNameCriteriaIdType: CriteriaIdType;
    outputCriteriaIdType: CriteriaIdType; // TODO remove after old pi is removed

    /* TODO not sure if to keep this or revert to outputCriteriaType. leave it for now */
    outputCriteriaIdTypes: CriteriaIdType[];
}

class MetricModelTransformer
    implements ModelTransformer<MetricDataItem, ExtMetricDataItem>
{
    constructor(
        private readonly _displayNameCriteriaIdType: CriteriaIdType,
        private readonly _outputCriteriaIdTypes: CriteriaIdType[],
    ) {
        if (this._displayNameCriteriaIdType === CriteriaIdType.QUEUE_ID_2) {
            this._displayNameCriteriaIdType = CriteriaIdType.QUEUE_ID;
        }
        this._outputCriteriaIdTypes = this._outputCriteriaIdTypes || [];
        if (this._outputCriteriaIdTypes.includes(CriteriaIdType.QUEUE_ID_2)) {
            this._outputCriteriaIdTypes = this._outputCriteriaIdTypes.map(
                (e) =>
                    e === CriteriaIdType.QUEUE_ID_2
                        ? CriteriaIdType.QUEUE_ID
                        : e,
            );
        }
    }

    transform(model: ExtMetricDataItem): MetricDataItem {
        if (model.primaryType === CriteriaIdType.QUEUE_ID_2) {
            model.primaryType = CriteriaIdType.QUEUE_ID;
        }
        if (model.secondaryType === CriteriaIdType.QUEUE_ID_2) {
            model.secondaryType = CriteriaIdType.QUEUE_ID;
        }
        if (model.tertiaryType === CriteriaIdType.QUEUE_ID_2) {
            model.tertiaryType = CriteriaIdType.QUEUE_ID;
        }

        const transformed: MetricDataItem = {
            id: model.metricID,
            name: model.metricName,
            shortName: model.metricShortName,
            initials: model.metricInitials,
            eventDate: new Date(model.eventDate || ''),
            actual: model.actual,
            target: model.target,
            previousActual: model.previousActual,
            projectedActual: model.projectedActual,
            projectedTarget: model.projectedTarget,
            projectedPctToTgt: model.projectedPctToTgt,
            gap: model.gap,
            goal: model.goal,
            rankOrder: model.rankOrder,
            targetThresholdColor: model.targetThresholdColour,
            targetThresholdBold:
                model.targetThresholdColour &&
                model.targetThresholdColour.toLowerCase() !== 'black' &&
                !model.targetThresholdColour.startsWith('#000'),
            targetThresholdName: model.targetThresholdName,
            trendClass: model.trend,
            trendName: model.trendName,
            displayId: null,
            displayName: null,
            displayShortName: null,
            displayInitials: null,
            dataId: null,
            dataIds: null,
            primaryId: model.primaryId,
            secondaryId: model.secondaryId,
            tertiaryId: model.tertiaryId,
            // last mile
            percentToTarget: percentToTarget(model.actual, model.target),
            objectiveMet: objectiveMet(
                model.actual,
                model.target,
                model.rankOrder,
            ),
            weight: null,
            weightedScore: null,
        };

        if (this._displayNameCriteriaIdType === model.primaryType) {
            transformed.displayId = model.primaryId;
            transformed.displayName = model.primaryName;
            transformed.displayShortName = model.primaryShortName;
            transformed.displayInitials = model.primaryInitials;
        }
        if (this._displayNameCriteriaIdType === model.secondaryType) {
            transformed.displayId = model.secondaryId;
            transformed.displayName = model.secondaryName;
            transformed.displayShortName = model.secondaryName;
            transformed.displayInitials = model.secondaryInitials;
        }
        if (this._displayNameCriteriaIdType === model.tertiaryType) {
            transformed.displayId = model.tertiaryId;
            transformed.displayName = model.tertiaryName;
            transformed.displayShortName = model.tertiaryName;
            transformed.displayInitials = model.tertiaryInitials;
        }

        transformed.dataId = this._outputCriteriaIdTypes.includes(
            model.primaryType,
        )
            ? model.primaryId
            : this._outputCriteriaIdTypes.includes(model.secondaryType)
            ? model.secondaryId
            : this._outputCriteriaIdTypes.includes(model.tertiaryType)
            ? model.tertiaryId
            : null;

        transformed.dataIds = {};
        if (this._outputCriteriaIdTypes.includes(model.primaryType)) {
            transformed.dataIds[model.primaryType] = model.primaryId;
        }
        if (this._outputCriteriaIdTypes.includes(model.secondaryType)) {
            transformed.dataIds[model.secondaryType] = model.secondaryId;
        }
        if (this._outputCriteriaIdTypes.includes(model.tertiaryType)) {
            transformed.dataIds[model.tertiaryType] = model.tertiaryId;
        }

        return transformed;
    }
}

export class MetricModelSetTransformer extends ModelSetTransformer<
    MetricData,
    ExtMetricDataItem
> {
    constructor(
        private readonly _displayNameCriteriaIdType: CriteriaIdType,
        private readonly _outputCriteriaIdTypes: CriteriaIdType[],
    ) {
        super();
    }

    protected newTransformer(): ModelTransformer<
        MetricData,
        ExtMetricDataItem
    > {
        return undefined;
    }

    transform(model: ExtMetricDataItem[]): MetricData[] {
        const transformer = new MetricModelTransformer(
            this._displayNameCriteriaIdType,
            this._outputCriteriaIdTypes,
        );
        const transformedMetrics: MetricDataItem[] = model.map((item) =>
            transformer.transform(item),
        );
        const transformed: MetricData[] = [];
        transformedMetrics.forEach((m) => {
            let di = transformed.find((value) => value.id === m.displayId);
            if (!di) {
                di = {
                    id: m.displayId,
                    name: m.displayName,
                    shortName: m.displayShortName,
                    initials: m.displayInitials,
                    dataId: m.dataId,
                    metrics: [],
                    displayNameCriteriaIdType: this._displayNameCriteriaIdType,
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    outputCriteriaIdType:
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        this._outputCriteriaIdTypes &&
                        this._outputCriteriaIdTypes.length
                            ? this._outputCriteriaIdTypes[0]
                            : null,
                    outputCriteriaIdTypes: this._outputCriteriaIdTypes
                        ? [...this._outputCriteriaIdTypes]
                        : null,
                };
                transformed.push(di);
            }
            di.metrics.push(m);
        });
        return transformed;
    }
}

export function validateMetric(model: MetricData[]): void {
    const valid = model != null && model.length !== 0;
    if (!valid) {
        throw new Error('MetricData not valid');
    }
}

function percentToTarget(
    actual: number,
    target: number,
    rankOrder?: number,
): number {
    let result = null;
    if (actual != null && target != null) {
        result =
            rankOrder != null
                ? ((actual - target) / target) * (rankOrder === 0 ? -1 : 1) + 1
                : actual / target;
        result =
            isNaN(result) || result === Infinity || result === -Infinity
                ? null
                : result;
    }
    return result;
}

export function percentToTargetRanked(
    actual: number,
    target: number,
    rankOrder: number,
): number {
    return percentToTarget(actual, target, rankOrder);
}

function objectiveMet(
    actual: number,
    target: number,
    rankOrder: number,
): boolean {
    const ptt = percentToTarget(actual, target);
    return rankOrder > 0 ? ptt >= 1 : ptt <= 1;
}

export function weightedScore(
    actual: number,
    target: number,
    weight: number,
): number {
    const ptt = percentToTarget(actual, target);
    const result = !ptt ? null : weight * ptt;
    return isNaN(result) || result === Infinity ? null : result;
}
